/**
 * Piece weights (data/pieces.json): what one piece weighs as it is bought, per product. For turning
 * pieces into grams and back: "1 Zwiebel" is about 120 g, "30 g Zwiebel" is less than one. A name
 * of the product may weigh differently (byName): an Eigelb is not a whole egg, a Gemüsezwiebel no
 * ordinary onion. Taken over from SpeisePlan on 18.10.2026, kept here since 22.10.2026.
 */
import data from "../data/pieces.json" with { type: "json" };
import { nameKey } from "./normalize.ts";

export interface Piece {
  /** Grams of one piece, rounded. */
  grams: number;
  /** The word for one piece: "Stk", "Zehe", "Stange", "Kopf". */
  unit: string;
}

type Entry = Piece & { byName?: Record<string, Piece> };

const PIECES: Readonly<Record<string, Entry>> = data.pieces;

export const PIECES_SOURCE: { repo: string; commit: string } = data.source;

/** Per product, the names with a weight of their own, by nameKey ("eigelb"). */
const BY_NAME = new Map(
  Object.entries(PIECES).map(([id, e]) => [id, new Map(Object.entries(e.byName ?? {}).map(([name, piece]) => [nameKey(name), piece]))]),
);

/**
 * The weight of one piece of a product, or undefined when it is not counted in pieces (or not
 * known). With the name the recipe or list used, a name of the product that weighs differently gets
 * its own weight: pieceWeight("ei", "Eigelb") is 20 g, pieceWeight("ei", "Eier") 60 g.
 */
export function pieceWeight(id: string, name?: string): Piece | undefined {
  if (!Object.hasOwn(PIECES, id)) return undefined;
  const own = name === undefined ? undefined : BY_NAME.get(id)?.get(nameKey(name));
  const { grams, unit } = own ?? PIECES[id]!;
  return { grams, unit };
}

/** The ids that have a piece weight. */
export const piecedProducts = (): string[] => Object.keys(PIECES);

/** The names of a product with a weight of their own. */
export const piecedNames = (id: string): string[] => Object.keys(PIECES[id]?.byName ?? {});
